import {useContext, useEffect, useMemo, useRef} from 'react'
import {SectionSearchBarContext} from './SearchBar'
import {useNavigate, useParams} from 'react-router-dom'
import {DeviceStatus, DeviceTypes, Modes, Roles, getInitials} from 'utility/utility'
import {filter, has} from 'lodash'
import {User} from './CreateUser'
import feathers from 'utility/client'
import {connectService} from 'components/service/connectService'
import {Indicator} from 'modules/device/components/Indicator'
import {canCreateRemote} from '../helpers'
import { PeopleAction } from '../components/PeopleAction'
import { useConfirm } from 'hooks/useConfirm'
import { useSelector } from 'react-redux'

export const People = connectService(
  props => {
    const navigate = useNavigate()
    const userModalRef = useRef(null)
    const confirm = useConfirm()
    const state = useSelector(state => state.state)

    const {distributor, reseller, business} = useParams()

    const {
      skip,
      total,
      limit,
      list,
      loading,
      Loader,
      Placeholder,
      Paginator,
      allowActions,
      business: businessData
    } = props
    const {setPlaceholder} = useContext(SectionSearchBarContext)

    useEffect(() => {
      setPlaceholder(`Showing ${skip + 1} to ${skip + limit} of ${total}`)
    }, [limit, setPlaceholder, skip, total])

    const onAdd = user => {
      navigate(`${DeviceTypes.REMOTE}/${Modes.CREATE}/${user._id}`)
    }

    const onEdit = user => {
      navigate(`${DeviceTypes.REMOTE}/${Modes.UPDATE}/${user._id}/${user.device._id}`)
    }

    const onRemove = user => {
      confirm({
        title: `Confirm Deletion of User account`,
        message: `Are you sure you want to remove ${user.firstName} ${user.lastName}? Once removed, account and associated device will be removed/disconnected from Video Window apps!`,
        onConfirm: async () => {
          await feathers.service('user').remove(user._id)
        }
      })
    }

    const canBusinessCreate = useMemo(() => {
      const usersHavingDevice = filter(list, l => has(l, 'device') && l.device)
      return canCreateRemote(business?.licenses?.remote, usersHavingDevice.length)
    }, [businessData, list])

    const makeAdmin = async user => {
      const updated = await feathers.service('user').patch(
        user._id,
        {
          $push: {
            permissions: Roles.business
          },
          adminOf: business,
          entity: 'business'
        },
        {
          query: {
            $populate: ['device', 'distributor', 'reseller', 'business', 'adminOf']
          }
        }
      )

      console.log(updated)
    }

    const revokeAdmin = async user => {
      const updated = await feathers.service('user').patch(
        user._id,
        {
          $pull: {
            permissions: Roles.business
          },
          $unset: {
            adminOf: '',
            entity: ''
          }
        },
        {
          query: {
            $populate: ['device', 'distributor', 'reseller', 'business', 'adminOf']
          }
        }
      )

      console.log(updated)
    }

    return (
      <div className="table-responsive">
        <table className="vw-table vw-tab-table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Email</th>
              {allowActions ? (
                <>
                  <th>Admin</th>
                  <th>Remote</th>
                </>
              ) : (
                <th className="text-center">Administrator</th>
              )}
            </tr>
          </thead>
          <tbody>
            {list.length ? (
              list.map(item => (
                <tr key={item._id} className="data-list">
                  <td>
                    <div className="vw-table__name">
                      <div className="vw-table__name__no-img vw-icon-bg position-relative">
                        {item.device ? (
                          <Indicator
                            device={item.device._id}
                            state={state?.[item.device._id] ? DeviceStatus.Live : DeviceStatus.Offline}
                            style={{position: 'absolute', top: 0, right: 2}}
                          />
                        ) : null}
                        {getInitials(`${item.firstName} ${item.lastName}`)}
                      </div>{' '}
                      {`${item.firstName} ${item.lastName}`}
                    </div>
                  </td>
                  <td>{item.email}</td>
                  <td className="text-center">
                    {!item?.adminOf || !item.entity ? (
                      <button className="d-inline btn btn-add btn-add--lg" onClick={() => makeAdmin(item)}>
                        <i className="fal fa-plus"></i>
                      </button>
                    ) : (
                      <div className="d-flex align-items-center">
                        <div className="mr-2">
                          <div className="fw-bold">Admin</div>
                          <small>{item?.adminOf?.name}</small>
                        </div>
                        <button className="btn-remove" onClick={() => revokeAdmin(item)}>
                          <i className="fal fa-minus"></i>
                        </button>
                      </div>

                    )}
                  </td>
                  <td className="text-right">
                    <PeopleAction
                      device={item?.device}
                      actionable={allowActions}
                      canCreate={canBusinessCreate}
                      onEdit={() => onEdit(item)}
                      onAdd={() => onAdd(item)}
                      onRemove={() => onRemove(item)}
                    />
                  </td>
                </tr>
              ))
            ) : loading ? (
              <Loader colSpan="4" />
            ) : (
              <Placeholder placeholder="No People" colSpan="4" />
            )}
          </tbody>
        </table>

        <div className="vw-group-button">
          <button
            className="btn btn-add btn-add--lg vw-tab-group-button"
            onClick={() => {
              const payload = {business}

              if (distributor) {
                payload.distributor = distributor
              }

              if (reseller) {
                payload.reseller = reseller
              }
              userModalRef.current.show(payload)
            }}
          >
            <i className="fal fa-plus"></i>
          </button>
        </div>

        <User
          ref={userModalRef}
          ModalProps={{
            confirmTitle: 'Save User',
            title: `Add User`
          }}
        />
        {Paginator({
          className: 'd-flex flex-row-reverse mt-2',
          sm: true
        })}
      </div>
    )
  },
  'user',
  {
    query: {
      $populate: ['device', 'business', 'reseller', 'distributor', 'adminOf'],
      $sort: {firstName: 1}
    },
    paramsAsQueryParams: ['distributor', 'reseller', 'business'],
    searchProperty: ['firstName', 'lastName', 'email']
  }
)
